// Tenant shell — invoiceTest's app frame, verbatim (owner 2026-09-05: "change the ui to how it is
// in invoiceTest for all the transplanted screens, do not make your own stuff, this includes the
// top bar"). Structure and classes come straight from invoiceTest/src/main.js L406-600
// (.shell-layout → scrim + .drawer + .shell-main(.topbar.app-bar + main.shell-content)), styled by
// public/brand/vt.css (the scoped invoiceTest stylesheet — scripts/build-vt-css.mjs). The ONLY
// deviations are the owner-sanctioned schema ones: real auth (user chip + sign-out replaces the
// sandbox role switcher), the multi-company switcher, i18n t() labels, the 🏛 FY-governance toggle
// (owner 2026-08-26), the bug-report chrome and the deploy-verify version badge.
//
// The shell adds class "vt" to <body> so vt.css only ever styles the tenant surface — the admin
// screens (aboo skin) and the login/home screens remove it (see src/admin/shell.js, home.js).
import { html, signal, computed, navigate } from '../shared/t4.js';
import { api } from '../shared/api.js';
import { currentUser, logout } from '../shared/auth.js';
import { idleWarning, idleCountdown, startIdleWatch, stopIdleWatch, stayActive } from '../shared/idle.js';
import { toggleTheme, theme } from '../shared/theme.js';
import { t, initI18n } from '../shared/i18n.js';
import { versionBadge, releaseNotesModal } from '../shared/version.js';
import { bugFab, bugReportModal } from '../shared/bugreport.js';
import { impersonating, impersonationBanner } from '../shared/impersonate.js';
import { mayLeave, recordNav, markCurrent, screenLabel } from '../shared/vtnav.js';
import { icon } from '../shared/icons.js';
import { companyRole, isCompanyAdmin, accessLevel } from '../shared/companyrole.js';
import { setBaseCurrencySymbol, setBaseCurrencyCode, setBaseCurrencyId } from './bank_helpers.js';

const K_CO = 'sleek_co_name_';
const K_CO_SYM = 'sleek_co_cursym_';   // cached base-currency symbol per company
const K_CO_CODE = 'sleek_co_curcode_'; // cached base-currency code and id per company (tell foreign from base)
const K_CO_CURID = 'sleek_co_curid_';
const navOpen = signal(false);   // mobile drawer state (reset on navigation)

// Header notification counts — Approvals awaiting a decision + Expense claims awaiting action
// (invoiceTest app-bar parity: two live-count shortcuts, main.js:505). Module-level so they
// survive the per-navigation shell re-render; refreshed on each shell mount.
const apprCount = signal(0), expCount = signal(0);
let countsFor = null, countsAt = 0;
async function loadHeaderCounts(companyId){
  const now = Date.now();
  if(countsFor === companyId && (now - countsAt) < 15000) return;
  countsFor = companyId; countsAt = now;
  // Badge what each role actually needs to act on: an admin sees the whole company's pending queue
  // (things awaiting THEIR decision); a non-admin sees only their OWN in-flight submissions
  // (?mine=1), never a company-wide count they can't action (owner 2026-09-10). The role is
  // normally cached by shell-mount time; if it resolves late the next refresh corrects the count.
  const mine = isCompanyAdmin(companyRole(companyId).value) ? '' : '&mine=1';
  try { const r = await api.get('/companies/' + companyId + '/approvals?state=pending&per_page=1' + mine); apprCount.value = (r && r.total) || 0; }
  catch { apprCount.value = 0; }
  try { const r = await api.get('/companies/' + companyId + '/claims?status=pending&per_page=1' + mine); expCount.value = (r && r.total) || 0; }
  catch { expCount.value = 0; }
}
export function refreshHeaderCounts(companyId){ countsAt = 0; loadHeaderCounts(companyId); }

// 🏛 governance toggle (owner 2026-08-26): opens Financial Years remembering where you were;
// pressed again (or that screen's Back) returns you exactly there.
let govReturnTo = null;
export function openGovernance(companyId){
  const govPath = '/co/' + companyId + '/governance';
  const here = location.pathname || '';
  if(here === govPath) return leaveGovernance(companyId);
  govReturnTo = here || null;
  navigate(govPath);
}
export function leaveGovernance(companyId){
  const back = govReturnTo || ('/co/' + companyId + '/dashboard');
  govReturnTo = null;
  navigate(back);
}

export function rememberCompany(id, name){
  try { sessionStorage.setItem(K_CO + id, name); } catch {}
}
export function companyName(id){
  const cached = (() => { try { return sessionStorage.getItem(K_CO + id); } catch { return null; } })();
  // Set the app-wide base-currency symbol (and code) synchronously from cache so money renders in the
  // company's currency on the very first paint (no symbol — never an assumed R — until /me lands on a
  // cold cache).
  const cachedSym = (() => { try { return sessionStorage.getItem(K_CO_SYM + id); } catch { return null; } })();
  const cachedCode = (() => { try { return sessionStorage.getItem(K_CO_CODE + id); } catch { return null; } })();
  const cachedCurId = (() => { try { return sessionStorage.getItem(K_CO_CURID + id); } catch { return null; } })();
  setBaseCurrencySymbol(cachedSym || '');   // '' clears the previous company's symbol
  setBaseCurrencyCode(cachedCode || '');    // …and its currency code and id
  setBaseCurrencyId(cachedCurId || '');
  const name = signal(cached || '');
  // Name the browser tab/window after the company, so several popped-out companies are tellable apart
  // in the taskbar / window switcher (owner 2026-09-24: pop companies out into separate windows).
  const titleFor = n => { if(n) document.title = n + ' · aboo'; };
  titleFor(cached);
  if(!cached || !cachedSym || !cachedCode || !cachedCurId){
    (async () => {
      try {
        const me = await api.get('/me');
        const co = (me.companies || []).find(c => String(c.id) === String(id));
        if(co){
          name.value = co.name; rememberCompany(id, co.name); titleFor(co.name);
          if(co.base_currency_symbol){
            setBaseCurrencySymbol(co.base_currency_symbol);
            try { sessionStorage.setItem(K_CO_SYM + id, co.base_currency_symbol); } catch {}
          }
          if(co.base_currency_code){
            setBaseCurrencyCode(co.base_currency_code);
            try { sessionStorage.setItem(K_CO_CODE + id, co.base_currency_code); } catch {}
          }
          if(co.base_currency_id !== null && co.base_currency_id !== undefined){
            setBaseCurrencyId(co.base_currency_id);
            try { sessionStorage.setItem(K_CO_CURID + id, String(co.base_currency_id)); } catch {}
          }
        }
      } catch {}
    })();
  }
  return name;
}

// Switch-company "undo": clicking the 🏢 pill leaves the company shell for the picker, which is easy
// to hit by mistake. Before leaving we stash where we were (company id + name + exact path) so the
// picker can offer a "← Back to <company>" return. Session-scoped and overwritten on each switch;
// the picker consumes it when the user opens any company or takes the return (owner 2026-09-15).
const K_RETURN = 'sleek_co_return';
export function rememberReturn(id, name, path){
  try { sessionStorage.setItem(K_RETURN, JSON.stringify({ id, name, path })); } catch {}
}
export function takeReturn(){
  try { const v = sessionStorage.getItem(K_RETURN); return v ? JSON.parse(v) : null; } catch { return null; }
}
export function clearReturn(){ try { sessionStorage.removeItem(K_RETURN); } catch {} }

// Drawer contents — invoiceTest's sidenavGroups() (main.js:428, Xero menu grouping), with the
// wave-only screens slotted into their nearest section: Batch invoicing → Sales; Bank accounts +
// Journals → Accounting; Settings → Setup. Chart of accounts lives inside Settings here (aboo
// admin seeds the CoA — an owner-sanctioned schema difference, so no nav item for it).
// Credit notes + Repeating are NOT drawer items — invoiceTest/Xero surface both from the invoices
// list's action row ("New credit note" / "Repeating", built in plan S5); their routes stay live for
// deep links.
function sidenavGroups(){
  return [
    { section: null, items: [
      // Icon names are Lucide keys — SVG data in src/shared/icons.js. See admin/shell.js for the
      // same treatment; consistent stroke / size / currentColor, no more Unicode-glyph mismatch.
      // Nav labels are colloquial (UK/US/AU vernacular) — see plan/vocab-and-expenses.md. Icon
      // names are Lucide keys — SVG data in src/shared/icons.js.
      { key: 'dashboard', label: 'Dashboard', tkey: 'common.dashboard', icon: 'layout-dashboard', screen: 'dashboard' },
      // NEW colloquial tkeys (nav.*_v2) — the old nav.documents/nav.batches DB entries carried the
      // formal wording ("Batch Invoicing" etc); switching to a fresh key lets us use the colloquial
      // fallback here without touching the admin translation table. Admin can seed nav.*_v2 later.
      // Split into two focused screens (2026-09-17): Sales lands on /invoices with the sales-side
      // family tabs (Quotes · Invoices · Credit Notes); Purchases lands on /purchase-orders with the
      // supplier-side tabs (Purchase Orders · Goods Received · Supplier Credit Notes). Bills (SINV) is a
      // third top-level item. All URLs and route handlers stay identical; only the nav grouping
      // changed. See plan/vocab-and-expenses.md.
      { key: 'sales', label: 'Sales', tkey: 'nav.sales_v2', icon: 'file-text', screen: 'all-sales' },
      { key: 'purchases', label: 'Purchases', tkey: 'nav.purchases_v2', icon: 'package', screen: 'all-purchases' },
      { key: 'bank', label: 'Banking', tkey: 'nav.banking_v2', icon: 'landmark', screen: 'bank' },
      { key: 'journals', label: 'Journals', tkey: 'common.journals', icon: 'arrow-left-right', screen: 'journals' },
      { key: 'approvals', label: 'Approvals', tkey: 'nav.approvals', icon: 'clipboard-check', screen: 'approvals',
        badge: () => apprCount.value },
      { key: 'expenses', label: 'Expenses', tkey: 'nav.expenses', icon: 'wallet', screen: 'expenses',
        badge: () => expCount.value },
      { key: 'products', label: 'Service items', tkey: 'nav.service_items_v2', icon: 'package', screen: 'products' },
      { key: 'batches', label: 'Batch Invoices', tkey: 'nav.batch_invoices_v2', icon: 'copy', screen: 'batches' },
    ] },
    { section: ['nav.business', 'Business'], items: [
      { key: 'contacts', label: 'Contacts', tkey: 'common.contacts', icon: 'contact', screen: 'contacts' },
    ] },
    { section: ['nav.reporting', 'Reporting'], items: [
      { key: 'reports', label: 'Reports', tkey: 'common.reports', icon: 'bar-chart-3', screen: 'reports' },
    ] },
    { section: ['nav.setup', 'Setup'], items: [
      { key: 'team', label: 'Team', tkey: 'nav.team', icon: 'users', screen: 'team', adminOnly: true },
      { key: 'settings', label: 'Settings', tkey: 'common.settings', icon: 'settings', screen: 'settings' },
    ] },
  ];
}

// The seven document families + the hub landing all live under ONE "Documents" nav item now
// (owner 2026-09-06). Any of these URL segments highlights Documents and reads "Documents" in
// breadcrumbs. Keep in sync with DOC_TYPES keys in documents.js.
// Nav item highlighting — three URL-segment sets, one per top-level document-family nav item.
// The old single DOC_SEGS is now split by side so Sales / Purchases each light up only for their
// own URLs. Bills has its own set. Kept as Sets so tests can compare shape-for-shape.
const SALES_SEGS      = new Set(['documents', 'invoices', 'quotes', 'credit-notes', 'all-sales', 'sales-memos']);
const PURCHASES_SEGS  = new Set(['purchase-orders', 'goods-received', 'supplier-credits', 'all-purchases']);
const BILLS_SEGS      = new Set(['bills']);
// Approvals + Expenses (the two notification badges) live under ONE "Notifications" hub item now
// (owner 2026-09-06), sub-tabbed like Contacts. Any of these highlights Notifications.
const NOTIF_SEGS = new Set(['notifications', 'approvals', 'expenses']);

// Which nav item is active, derived from the URL (path + the products preset).
function activeKeyFromUrl(){
  const path = typeof location !== 'undefined' ? location.pathname : '';
  const m = path.match(/^\/(?:co|companies)\/[^/]+\/([^/?]+)/);
  const seg = m ? m[1] : '';
  if(SALES_SEGS.has(seg)) return 'sales';
  if(PURCHASES_SEGS.has(seg)) return 'purchases';
  if(BILLS_SEGS.has(seg)) return 'bills';
  // Approvals + Expenses are their own nav items now; the legacy /notifications landing → approvals.
  if(seg === 'expenses') return 'expenses';
  if(seg === 'approvals' || seg === 'notifications') return 'approvals';
  // Customers/Suppliers are just Contacts filters now (their own nav items were removed) — every
  // contacts URL highlights the single Contacts item.
  if(seg === 'contacts') return 'contacts';
  if(seg === 'products') return 'products';
  if(seg.startsWith('bank')) return 'bank';
  if(seg === 'accounts' || seg === 'import') return 'settings';
  if(seg === 'governance') return 'settings';
  return seg || 'dashboard';
}

// The Notifications hub tab strip — Approvals + Expenses (the two notification badges) as sub-tabs,
// mirroring how Contacts splits customers/suppliers (owner 2026-09-06). Each tab carries its live
// pending count and links to its own screen, which renders this same strip at its head. Uses the
// .hub-tabs pill styling (the Documents type-tabs moved to their own .doctabs arrow ribbon on
// 2026-09-09, so this hub keeps .hub-tab to itself).
export function notificationsTabs(companyId, activeKey){
  // Approvals FIRST (green), Expenses SECOND (red) — owner 2026-09-06. Each tab carries its live
  // count as a coloured .notif-dot (the two circles the owner asked to see next to the header).
  const TABS = [
    ['approvals', 'nav.approvals', 'Approvals', apprCount, 'appr'],
    ['expenses',  'nav.expenses',  'Expenses',  expCount,  'exp'],
  ];
  return html`<div class="hub-tabs" role="tablist" aria-label=${t('nav.notifications', 'Approvals and Expenses')}>
    <div class="hub-tab-grp">
      ${TABS.map(([key, tkey, fallback, count, kind]) => {
        const on = key === activeKey;
        return html`<button type="button" role="tab" aria-selected=${on ? 'true' : 'false'}
          class=${'hub-tab' + (on ? ' active' : '')}
          @click=${() => { if(on) return;
            const to = '/co/' + companyId + '/' + key;
            navigate(to); }}>${t(tkey, fallback)}${
          () => count.value > 0 ? html`<span class=${'notif-dot ' + kind}>${count.value}</span>` : ''}</button>`;
      })}
    </div>
  </div>`;
}

// (+ New ▾ create-document menu removed from the top bar 2026-09-15 — owner. Create still reachable
// from each list screen's actions and the app-bar search palette below.)

// (Approvals/Expenses top-bar shortcut buttons removed 2026-09-15 — owner: they live in the side
// nav + Documents already. The live counts still feed the side-nav badges + notificationsTabs.)

let lastPath = null;   // recordNav bookkeeping across shell mounts

// ── Main-menu place memory (owner 2026-09-24) ───────────────────────────────────────────────────────
// "If I was in Reports → TB and flip to Bank Accounts, when I click back to Reports I am still on the
// TB with all the settings I had before." Each main-menu section remembers the FULL url (screen +
// filters) you were last on; clicking into that section returns you there. Per company, per browser
// tab (sessionStorage). Editors (/new, /edit) are never restored — leaving one was deliberate and
// returning risks a double-post (same rule as the ← trail).
const K_SECTION = 'sleek_nav_section_';
const NEVER_RESTORE_RE = /\/(new|edit)(\/|$)/;
function readSections(companyId){
  try { return JSON.parse(sessionStorage.getItem(K_SECTION + companyId) || '{}') || {}; } catch { return {}; }
}
function rememberSection(companyId, key, url){
  if(!companyId || !key || !url) return;
  const m = readSections(companyId);
  m[key] = url;
  try { sessionStorage.setItem(K_SECTION + companyId, JSON.stringify(m)); } catch { /* storage off */ }
}
/** A screen that keeps its filters in its OWN url via history.replaceState (e.g. the Trial Balance)
 *  calls this after each change, so both the ← trail and the main-menu memory hold the CURRENT
 *  filters, not the ones the screen mounted with. */
export function noteUrlChange(companyId){
  const here = location.pathname + (location.search || '');
  // Only once THIS screen's shell has mounted (same path, new query). A screen's first load runs
  // before its shell mounts, when lastPath is still the PREVIOUS screen — the mount must record that
  // on the ← trail, so it must not be overwritten here.
  if(lastPath && lastPath.split('?')[0] === location.pathname) lastPath = here;
  rememberSection(companyId, activeKeyFromUrl(), here);
}

export function tenantShell(companyId, title, content){
  initI18n(companyId);
  document.body.classList.add('vt');            // gate vt.css onto the tenant surface
  const coName = companyName(companyId);
  const coRole = companyRole(companyId);
  const signingOut = signal(false);
  const active = activeKeyFromUrl();
  loadHeaderCounts(companyId);

  // ← where-you-came-from bookkeeping (vtnav): a mount on a NEW path is a real move.
  const here = location.pathname + (location.search || '');
  // Record the FULL url (path + query) so a ← returns to the exact filtered view — the tab/focus/
  // aging-bucket the user was looking at, not a bare list reset to 'all' (owner 2026-09-06).
  if(lastPath && lastPath !== here) recordNav(lastPath, titleForPath(lastPath), here);
  lastPath = here;
  markCurrent(here);   // track this screen's live scroll so back-nav restores position (vtnav)
  rememberSection(companyId, active, here);   // main-menu place memory: this section is now "here"

  function titleForPath(p){
    const own = screenLabel(p);           // a screen that named itself (a report: "General Ledger")
    if(own) return own;
    const m = String(p).match(/^\/(?:co|companies)\/[^/]+\/([^/?]+)/);
    const seg = m ? m[1] : '';
    if(SALES_SEGS.has(seg)) return t('nav.sales_v2', 'Sales');
    if(PURCHASES_SEGS.has(seg)) return t('nav.purchases_v2', 'Purchases');
    if(BILLS_SEGS.has(seg)) return t('nav.bills_v2', 'Bills');
    if(NOTIF_SEGS.has(seg)) return t('nav.notifications', 'Approvals and Expenses');
    let found = null;
    sidenavGroups().forEach(g => g.items.forEach(it => { if(it.screen.split('?')[0] === seg && !found) found = t(it.tkey, it.label); }));
    return found || seg || 'Dashboard';
  }

  const groups = sidenavGroups();
  let pageTitle = title || '';
  groups.forEach(g => g.items.forEach(it => { if(it.key === active) pageTitle = t(it.tkey, it.label); }));

  function goItem(it){
    navOpen.value = false;
    const root = '/co/' + companyId + '/' + it.screen;
    const cur = activeKeyFromUrl();
    // record where we're leaving with its LIVE filters (a screen may have replaceState'd them since mount)
    rememberSection(companyId, cur, location.pathname + (location.search || ''));
    let to = root;
    // A DIFFERENT section → return to where you were in it. The section you're ALREADY in → its front
    // page (a fresh start), so e.g. the Reports list stays one click away.
    if(it.key !== cur){
      const last = readSections(companyId)[it.key];
      if(last && last.startsWith('/co/' + companyId + '/') && !NEVER_RESTORE_RE.test(last.split('?')[0])) to = last;
    }
    if(!mayLeave(to)) return;
    // Reopening a page BELOW the section's front page (the report you had open): the front page goes
    // in between, on the ← trail and in the browser history, so Back from that page lands on the
    // section's front page — the Reports list — not on the section you came from (owner 2026-10-08:
    // "it takes me to the settings???").
    const toPath = to.split('?')[0];
    if(toPath !== root && toPath.startsWith(root + '/')){
      const from = lastPath || (location.pathname + (location.search || ''));
      recordNav(from, titleForPath(from), root);
      lastPath = root;                    // this frame's next mount records root → the reopened page
      history.pushState(null, '', root);
    }
    navigate(to);
  }
  function signOut(){ document.body.classList.remove('vt'); stopIdleWatch(); logout(); navigate('/login'); }
  startIdleWatch(signOut);   // 15 min idle in every aboo window → 60 s "Still there?" → sign out (idempotent)

  const layoutClass = computed(() => 'shell-layout' + (navOpen.value ? ' nav-open' : '') + (impersonating.value ? ' imp-on' : ''));

  const frag = html`
    <div class=${layoutClass}>
      <div class="scrim" @click=${() => { navOpen.value = false; }}></div>

      <aside class="drawer">
        <div class="brand drawer-brand"><span class="dot"></span><b>aboo</b><span class="sub">books</span></div>
        <nav class="nav-list">
          ${() => sidenavGroups().map(group => {
            // admin-only items (Team) are hidden for normal members; drop an emptied section header too.
            const admin = isCompanyAdmin(coRole.value);
            const items = group.items.filter(it => !it.adminOnly || admin);
            if(!items.length) return [];
            return html`
            ${group.section ? html`<div class="nav-section">${t(group.section[0], group.section[1])}</div>` : []}
            ${items.map(it => html`
              <div class=${'nav-item' + (active === it.key ? ' active' : '')} role="button" tabindex="0"
                   @click=${() => goItem(it)}
                   @keydown=${e => { if(e.key === 'Enter' || e.key === ' '){ e.preventDefault(); goItem(it); } }}>
                <span class=${'nav-ico nav-ico-' + it.key}>${icon(it.icon)}</span>
                <span class="nav-lbl">${t(it.tkey, it.label)}</span>
                ${it.badge && it.badge() ? html`<span class="nav-badge">${it.badge()}</span>` : []}
              </div>`)}`;
          })}
        </nav>
        <div class="t-nav-foot">
          <div class="t-nav-version">${versionBadge('long')}</div>
        </div>
      </aside>

      <div class="shell-main">
        <header class="topbar app-bar">
          <div class="row-actions app-bar-left">
            <button type="button" class="app-title-wrap app-title-home"
                    title=${() => (coName.value || '') + ' — ' + t('common.go_to_dashboard', 'Go to dashboard')}
                    aria-label=${t('common.go_to_dashboard', 'Go to dashboard')}
                    @click=${() => { const to = '/co/' + companyId + '/dashboard';
                                     if(!mayLeave(to)) return;
                                     navigate(to); }}>
              <div class="app-company">${() => coName.value || ''}</div>
              <div class="app-title">${pageTitle}</div>
            </button>
          </div>
          <div class="row-actions">
            <div class="role-switch">
              <!-- three icon stubs on the LEFT of the user (owner 2026-09-15): switch-company · year-lock · theme -->
              <button class="btn theme-toggle" title=${() => coName.value ? coName.value + ' — ' + t('common.switch_company', 'Switch company') : t('common.switch_company', 'Switch company')}
                aria-label=${t('common.switch_company', 'Switch company')}
                @click=${() => { if(mayLeave('/home')){ rememberReturn(companyId, coName.value, location.pathname + (location.search || '')); document.body.classList.remove('vt'); navigate('/home'); } }}>${icon('briefcase', {size: 18})}</button>
              <button class="btn theme-toggle" title=${t('gov.governance_hint', 'Financial-year governance (lock / unlock years)')}
                aria-label=${t('gov.governance', 'Financial-year governance')}
                @click=${() => openGovernance(companyId)}>${icon('lock', {size: 18})}</button>
              <button class="btn theme-toggle" title=${t('common.toggle_theme', 'Switch between light and dark')}
                aria-label=${t('common.toggle_theme', 'Switch between light and dark')}
                @click=${() => toggleTheme()}>${() => icon(theme.value === 'dark' ? 'sun' : 'moon', {size: 18})}</button>
              <span class="user-name">${currentUser}</span>
              ${() => {
                const r = coRole.value; if(!r) return '';
                // Three access levels: Owner / Admin / User (owner directive 2026-09-11). An owner
                // is shown as "Owner", not "Admin" — the old label made a self-signup owner think
                // they were a platform admin (issue #60).
                const cfg = { owner: ['role.owner', 'Owner', 'role-owner'],
                              admin: ['role.admin', 'Admin', 'role-admin'],
                              user: ['role.user', 'User', 'role-member'] }[accessLevel(r)];
                return html`<span class=${'role-badge ' + cfg[2]} title=${r.name || ''}>${t(cfg[0], cfg[1])}</span>`;
              }}
              ${() => signingOut.value
                ? html`<span class="confirm-inline"><span class="confirm-q">${t('common.sign_out_confirm', 'Sign out?')}</span>
                    <button type="button" class="btn-confirm-yes" @click=${() => { signingOut.value = false; signOut(); }}>${t('common.yes', 'Yes')}</button>
                    <button type="button" class="btn-confirm-no" @click=${() => { signingOut.value = false; }}>${t('common.no', 'No')}</button></span>`
                : html`<button class="btn sm" @click=${() => { signingOut.value = true; }}>${t('common.sign_out', 'Sign out')}</button>`}
            </div>
          </div>
        </header>
        <main class="shell-content">
          ${content}
        </main>
      </div>

      <!-- Mobile: persistent horizontal-scrolling nav bar at the bottom of the viewport
           (owner Andre 2026-09-17: "make a scrolling menu at the bottom for mobile" +
           "I don't like the hamburger menu on mobile" + "free the space up at the top").
           The bar renders the same items as the desktop drawer, just laid out horizontally.
           Hidden above the mobile breakpoint by vt.css. -->
      <nav class="mobile-nav" aria-label=${t('nav.primary', 'Primary')}>
        <div class="mobile-nav-scroll">
          ${() => sidenavGroups().flatMap(group => {
            const admin = isCompanyAdmin(coRole.value);
            return group.items.filter(it => !it.adminOnly || admin).map(it => html`
              <button type="button" class=${'mobile-nav-item' + (active === it.key ? ' active' : '')}
                @click=${() => goItem(it)}
                aria-current=${active === it.key ? 'page' : 'false'}>
                <span class="mobile-nav-ico">${icon(it.icon, {size: 20})}</span>
                <span class="mobile-nav-lbl">${t(it.tkey, it.label)}</span>
                ${it.badge && it.badge() ? html`<span class="mobile-nav-dot">${it.badge()}</span>` : ''}
              </button>`);
          })}
        </div>
      </nav>

      ${releaseNotesModal()}
      ${() => idleWarning.value ? html`
        <div class="modal-overlay idle-overlay">
          <div class="modal-card idle-card">
            <div class="modal-head"><h3>${t('idle.title', 'Still there?')}</h3></div>
            <p class="idle-msg">${t('idle.msg', "You've been inactive for a while. For your security we'll sign you out in")}
              <b>${idleCountdown}</b> ${t('idle.seconds', 'seconds')}.</p>
            <div class="form-actions">
              <button type="button" class="btn btn-outline" @click=${signOut}>${t('idle.sign_out_now', 'Sign out now')}</button>
              <button type="button" class="btn btn-primary" @click=${() => stayActive()}>${t('idle.stay', 'Stay signed in')}</button>
            </div>
          </div>
        </div>` : ''}
      ${bugFab(companyId)}
      ${bugReportModal()}
      ${impersonationBanner()}
    </div>`;

  // Once the mobile horizontal-scroll bar is mounted, snap the active item into view so a
  // returning visit to a mid/late nav item (Reports, Settings) shows up on screen rather
  // than being scrolled off the right.
  const activeMobile = frag.querySelector('.mobile-nav-item.active');
  if(activeMobile) requestAnimationFrame(() => {
    try { activeMobile.scrollIntoView({ block: 'nearest', inline: 'center' }); } catch(_){}
  });

  return frag;
}
